// src/components/features/Elevation/components/shared/ElevationSwipeStep.jsx
// Generic "swipe left/right + tap thumbnail to select" step — bilkul wahi UI
// pattern jo ElevationStep1Theme.jsx me use hua tha. Isse har naye swipe-style
// step (Lights, CNC/Terracotta, Material, Ceiling, Parapet, Pergola) me
// wahi 200+ line ka swipe-logic dobara likhna nahi padta.

import {
  Box, Flex, Text, Button, VStack, HStack, Badge, IconButton, Icon,
} from "@chakra-ui/react";
import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { FiMaximize2, FiX } from "react-icons/fi";
import SmartImage from "./SmartImage";

// ─── Fullscreen zoom overlay ──────────────────────────────────────────────────
// Rendered via a portal straight into document.body — the swipe card below it
// has an active CSS `transform` (for the drag animation), and any
// position:fixed element inside a transformed ancestor gets contained by
// that ancestor instead of the real viewport, so a portal is needed here to
// get a true fullscreen overlay.
function ZoomOverlay({ src, alt, onClose }) {
  return createPortal(
    <Box
      position="fixed" inset={0} zIndex={2000}
      bg="blackAlpha.900"
      display="flex" alignItems="center" justifyContent="center"
      onClick={onClose}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <IconButton
        aria-label="Close"
        icon={<Icon as={FiX} boxSize="20px" />}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        position="absolute" top={4} right={4}
        borderRadius="full" bg="whiteAlpha.200" color="white"
        _hover={{ bg: "whiteAlpha.300" }}
      />
      <Box
        as="img"
        src={src}
        alt={alt}
        maxW="94vw"
        maxH="86vh"
        objectFit="contain"
        borderRadius="8px"
        onClick={(e) => e.stopPropagation()}
      />
    </Box>,
    document.body
  );
}

// ─── Swipe Card ───────────────────────────────────────────────────────────
function SwipeCard({ item, onLike, onDislike, current, total }) {
  const startX  = useRef(null);
  const [drag,  setDrag]  = useState(0);
  const [going, setGoing] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);

  const THRESHOLD = 70;
  const getX = (e) => (e.touches ? e.touches[0]?.clientX : e.clientX);

  const onStart = (e) => { startX.current = getX(e); };
  const onMove  = (e) => {
    if (startX.current === null) return;
    setDrag(getX(e) - startX.current);
  };
  const onEnd = () => {
    if (drag > THRESHOLD) {
      setGoing("right");
      setTimeout(() => { setDrag(0); setGoing(null); onLike(); }, 260);
    } else if (drag < -THRESHOLD) {
      setGoing("left");
      setTimeout(() => { setDrag(0); setGoing(null); onDislike(); }, 260);
    } else {
      setDrag(0);
    }
    startX.current = null;
  };

  const tx  = going === "left" ? -420 : going === "right" ? 420 : drag;
  const rot = Math.min(Math.max(drag / 14, -14), 14);

  return (
    <Box
      borderRadius="12px"
      overflow="hidden"
      border="1px solid #E2E8F0"
      boxShadow="md"
      cursor="grab"
      userSelect="none"
      style={{
        transform: `translateX(${tx}px) rotate(${rot}deg)`,
        opacity: going ? 0 : 1,
        transition: going ? "all 0.26s ease" : "transform 0.05s linear",
      }}
      onMouseDown={onStart} onMouseMove={onMove}
      onMouseUp={onEnd}     onMouseLeave={onEnd}
      onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
    >
      <Box h="190px" position="relative" bg="gray.100">
        <SmartImage src={item.img} alt={item.label} />

        {/* Expand / zoom-in button, top-left — lets the user view the
            image bigger without it being mistaken for a swipe gesture */}
        <IconButton
          aria-label="View bigger"
          icon={<Icon as={FiMaximize2} boxSize="13px" />}
          size="xs"
          position="absolute" top={2} left={2}
          bg="blackAlpha.600" color="white"
          borderRadius="full"
          _hover={{ bg: "blackAlpha.700" }}
          onClick={(e) => { e.stopPropagation(); setZoomOpen(true); }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        />
        {zoomOpen && (
          <ZoomOverlay src={item.img} alt={item.label} onClose={() => setZoomOpen(false)} />
        )}

        {/* Live swipe feedback — whole card washes green/red instead of a
            small corner label, so the feedback is impossible to miss */}
        {drag !== 0 && (
          <Box
            position="absolute" inset={0}
            bg={drag > 0 ? "green.500" : "red.500"}
            opacity={Math.min(Math.abs(drag) / 140, 0.55)}
            transition="opacity 0.05s linear"
            pointerEvents="none"
          />
        )}
        {Math.abs(drag) > 25 && (
          <Flex position="absolute" inset={0} align="center" justify="center" pointerEvents="none">
            <Flex
              align="center" justify="center"
              boxSize="60px" borderRadius="full"
              bg="whiteAlpha.900"
              boxShadow="0 6px 18px rgba(0,0,0,0.3)"
              transform={`scale(${Math.min(0.7 + Math.abs(drag) / 200, 1.1)}) rotate(${drag > 0 ? 8 : -8}deg)`}
              transition="transform 0.05s linear"
            >
              <Text fontSize="26px" lineHeight="1" fontWeight="800" color={drag > 0 ? "green.500" : "red.500"}>
                {drag > 0 ? "✓" : "✕"}
              </Text>
            </Flex>
          </Flex>
        )}

        <Box position="absolute" top={2} right={2} bg="blackAlpha.600" px={2} py={0.5} borderRadius="20px">
          <Text fontSize="10px" color="white" fontWeight="600">{current} / {total}</Text>
        </Box>

        <Flex
          position="absolute" bottom={0} left={0} right={0}
          justify="space-between" align="center"
          px={3} py={2}
          bgGradient="linear(to-t, blackAlpha.700, transparent)"
        >
          <Flex align="center" gap={1}>
            <Box w="18px" h="18px" borderRadius="full" bg="red.400"
              display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <Text fontSize="9px" color="white" fontWeight="800">✕</Text>
            </Box>
            <Text fontSize="9px" color="whiteAlpha.900">Swipe left if you Don't like the image</Text>
          </Flex>
          <Flex align="center" gap={1}>
            <Text fontSize="9px" color="whiteAlpha.900">Swipe Right if you like the image</Text>
            <Box w="18px" h="18px" borderRadius="full" bg="green.400"
              display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <Text fontSize="9px" color="white" fontWeight="800">✓</Text>
            </Box>
          </Flex>
        </Flex>
      </Box>

      {item.tags?.length > 0 && (
        <Box bg="white" px={3} py={2}>
          <HStack spacing={1} flexWrap="wrap">
            {item.tags.map((tag) => (
              <Badge key={tag} colorScheme="cyan" variant="subtle" fontSize="10px" px={2} py={0.5} borderRadius="5px">
                {tag}
              </Badge>
            ))}
            <Badge colorScheme="gray" variant="outline" fontSize="10px" px={1.5} borderRadius="5px" cursor="default">
              ⓘ Given tag, defines this image
            </Badge>
          </HStack>
        </Box>
      )}
    </Box>
  );
}

// ─── Main reusable step ────────────────────────────────────────────────────
// Props:
//   title, subtitle          — heading text
//   items                    — [{ id, label, tags, img }]
//   selectedFieldKey          — formData key for the selected item id
//   likedFieldKey (optional)  — formData key for array of liked ids
//   formData, setFormData, onNext, onBack, isLastStep, onSubmit
export default function ElevationSwipeStep({
  title, subtitle, items,
  selectedFieldKey, likedFieldKey,
  formData, setFormData, onNext, onBack, isLastStep, onSubmit,
}) {
  const [cardIndex, setCardIndex] = useState(0);
  const [likedIds,  setLikedIds]  = useState(
    likedFieldKey ? (formData?.[likedFieldKey] || []) : []
  );
  const selectedId = formData?.[selectedFieldKey] || "";

  const currentItem = items[cardIndex] ?? null;
  const allSwiped    = cardIndex >= items.length;

  const handleLike = () => {
    const id = items[cardIndex]?.id;
    if (!id) return;
    const newLiked = [...likedIds, id];
    setLikedIds(newLiked);
    setFormData({
      ...formData,
      [selectedFieldKey]: id,
      ...(likedFieldKey ? { [likedFieldKey]: newLiked } : {}),
    });
    setCardIndex((i) => i + 1);
  };

  const handleDislike = () => setCardIndex((i) => i + 1);

  const handleThumbSelect = (id) => {
    setFormData({ ...formData, [selectedFieldKey]: id });
  };

  const handleNext = () => {
    if (!selectedId) return;
    if (isLastStep) onSubmit(); else onNext();
  };

  const selectedItem = items.find((t) => t.id === selectedId);

  return (
    <Box
      h="600px" w="100%" maxW="400px" mx="auto"
      display="flex" flexDirection="column" justifyContent="space-between"
      px={4} py={6}
    >
      <Box
        flex="1" minH="0" overflowY="auto" overflowX="hidden" pr={1}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
        }}
      >
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="17px" fontWeight="600" color="gray.800">{title}</Text>
            <Text fontSize="12px" color="gray.500" mt={0.5}>{subtitle}</Text>
          </Box>

          {!allSwiped ? (
            <SwipeCard
              key={cardIndex}
              item={currentItem}
              onLike={handleLike}
              onDislike={handleDislike}
              current={cardIndex + 1}
              total={items.length}
            />
          ) : (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="10px" px={3} py={3} textAlign="center">
              <Text fontSize="13px" color="green.700" fontWeight="600">✓ All options reviewed</Text>
              <Text fontSize="11px" color="green.600" mt={0.5}>Select your preferred option from the thumbnails below</Text>
            </Box>
          )}

          {!allSwiped && (
            <Flex gap={3} justify="center" align="center">
              <IconButton
                onClick={handleDislike} aria-label="Dislike"
                borderRadius="full" w="44px" h="44px"
                bg="red.50" color="red.500" border="2px solid" borderColor="red.200"
                _hover={{ bg: "red.100" }}
                icon={<Text fontSize="18px" lineHeight="1" mb="1px">✕</Text>}
              />
              <Text fontSize="11px" color="gray.400" fontWeight="500">{cardIndex + 1} / {items.length}</Text>
              <IconButton
                onClick={handleLike} aria-label="Like"
                borderRadius="full" w="44px" h="44px"
                bg="green.50" color="green.500" border="2px solid" borderColor="green.200"
                _hover={{ bg: "green.100" }}
                icon={<Text fontSize="18px" lineHeight="1" mb="1px">✓</Text>}
              />
            </Flex>
          )}

          <Box>
            <Flex justify="space-between" align="center" mb={1.5}>
              <Text fontSize="11px" color="gray.500" fontWeight="600">
                {allSwiped ? "Tap any image to select your option" : "Browse all options"}
              </Text>
              <Text fontSize="10px" color="gray.400">{items.length} options</Text>
            </Flex>

            {/* legend — explains what the badges below mean */}
            <Flex gap={3} mb={2}>
              <Flex align="center" gap={1}>
                <Flex boxSize="14px" borderRadius="full" bg="cyan.500" align="center" justify="center">
                  <Text fontSize="8px" color="white" fontWeight="800">✓</Text>
                </Flex>
                <Text fontSize="9px" color="gray.500">Selected</Text>
              </Flex>
              <Flex align="center" gap={1}>
                <Flex boxSize="14px" borderRadius="full" bg="pink.400" align="center" justify="center">
                  <Text fontSize="8px" color="white">♥</Text>
                </Flex>
                <Text fontSize="9px" color="gray.500">Liked</Text>
              </Flex>
              <Flex align="center" gap={1}>
                <Box boxSize="10px" borderRadius="3px" border="2px solid" borderColor="yellow.400" />
                <Text fontSize="9px" color="gray.500">Viewing now</Text>
              </Flex>
            </Flex>

            {/* all thumbnails visible at once — wraps to multiple rows
                instead of horizontal-scrolling, so nothing looks "missing" */}
            <Flex gap="8px" flexWrap="wrap">
              {items.map((t, idx) => {
                const isSelected = selectedId === t.id;
                const isLiked    = likedIds.includes(t.id);
                const isCurrent  = !allSwiped && cardIndex === idx;
                return (
                  <Box
                    key={t.id}
                    position="relative" w="58px" h="58px"
                    borderRadius="9px" overflow="hidden" bg="gray.100"
                    border="2px solid"
                    borderColor={
                      isSelected ? "cyan.500"
                      : isCurrent  ? "yellow.400"
                      : "transparent"
                    }
                    boxShadow={isSelected ? "0 0 0 2px #B2F5EA" : "0 1px 3px rgba(0,0,0,0.12)"}
                    cursor="pointer" flexShrink={0}
                    onClick={() => handleThumbSelect(t.id)}
                    transition="all 0.15s ease"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "0 4px 10px rgba(0,0,0,0.18)" }}
                  >
                    <SmartImage src={t.img} alt={t.label} />

                    {/* index badge, top-left */}
                    <Box position="absolute" top="2px" left="2px" bg="blackAlpha.600" borderRadius="4px" px="3px" py="0.5px">
                      <Text fontSize="7px" color="white" fontWeight="700">{idx + 1}</Text>
                    </Box>

                    {/* selected: filled tint + checkmark badge */}
                    {isSelected && (
                      <>
                        <Box position="absolute" inset={0} bg="cyan.500" opacity={0.22} />
                        <Flex
                          position="absolute" bottom="2px" right="2px"
                          boxSize="16px" borderRadius="full" bg="cyan.500"
                          align="center" justify="center"
                          boxShadow="0 1px 4px rgba(0,0,0,0.35)"
                        >
                          <Text fontSize="9px" color="white" fontWeight="800">✓</Text>
                        </Flex>
                      </>
                    )}

                    {/* liked (not selected): heart badge */}
                    {isLiked && !isSelected && (
                      <Flex
                        position="absolute" bottom="2px" right="2px"
                        boxSize="15px" borderRadius="full" bg="pink.400"
                        align="center" justify="center"
                        boxShadow="0 1px 4px rgba(0,0,0,0.35)"
                      >
                        <Text fontSize="8px" color="white">♥</Text>
                      </Flex>
                    )}

                    {/* currently viewing (not selected): glowing ring */}
                    {isCurrent && !isSelected && (
                      <Box position="absolute" inset={0} boxShadow="inset 0 0 0 2px #ECC94B" />
                    )}
                  </Box>
                );
              })}
            </Flex>
          </Box>

          {selectedItem && (
            <Box border="2px solid #319795" borderRadius="10px" bg="cyan.50" px={3} py={2}>
              <Flex align="center" gap={2}>
                <Box w="36px" h="28px" borderRadius="5px" flexShrink={0} position="relative" overflow="hidden" bg="gray.100">
                  <SmartImage src={selectedItem.img} alt={selectedItem.label} />
                </Box>
                <Box>
                  <Text fontSize="13px" fontWeight="700" color="cyan.700">Selected: {selectedItem.label}</Text>
                  {selectedItem.tags?.length > 0 && (
                    <HStack spacing={1} flexWrap="wrap" mt={0.5}>
                      {selectedItem.tags.map((tag) => (
                        <Badge key={tag} colorScheme="cyan" variant="subtle" fontSize="9px" px={1.5} borderRadius="4px">{tag}</Badge>
                      ))}
                    </HStack>
                  )}
                </Box>
              </Flex>
            </Box>
          )}
        </VStack>
      </Box>

      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button onClick={onBack} variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500" borderRadius="6px"
          minW="100px" h="40px" fontWeight="500" fontSize="14px" _hover={{ bg: "cyan.50" }}>
          ← Previous
        </Button>
        <Button onClick={handleNext} isDisabled={!selectedId} variant="solid"
          bg={selectedId ? "cyan.500" : "gray.300"} color="white"
          borderRadius="6px" minW="100px" h="40px" fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: selectedId ? "cyan.600" : "gray.300" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}>
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}